import type React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	type FullPageSearchNetworkRefs,
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../events/vital-stats-events';

export type CreateFullPageSearchVitalStatsArgs = {
	searchSessionId: string | null | undefined;
	fireAnalyticsEvent: (event: any) => void;
	fullPageSearchTaskIdRef: React.MutableRefObject<string | null>;
	fullPageSearchNetworkRefs: FullPageSearchNetworkRefs;
	cacheAvailable: boolean;
	isNetworkMode: boolean;
};

export type CreateFullPageSearchNetworkVitalStatsArgs = {
	searchSessionId: string | null | undefined;
	fireAnalyticsEvent: (event: any) => void;
	fullPageSearchNetworkRefs: FullPageSearchNetworkRefs;
};

/**
 * Creates callbacks for Full Page Search VitalStats events (UX + Network).
 *
 * Keeps the orchestration logic out of query-plan so setupSearchExecution doesn't grow unbounded.
 *
 * External behavior is preserved:
 * - Uses the same feature flags
 * - Uses the same task names
 * - Fires identical taskStart/taskAbort events with the same moreContext attributes
 */
export function createFullPageSearchNetworkVitalStats({
	searchSessionId,
	fireAnalyticsEvent,
	fullPageSearchNetworkRefs,
}: CreateFullPageSearchNetworkVitalStatsArgs) {
	const isEnabled = () => Boolean(searchSessionId) && fg('rovo-search-vitalstats-fullpage-search-network');

	const getTimeTaken = (): number | undefined => {
		const startAt = fullPageSearchNetworkRefs.current.startAt;
		return startAt ? Math.max(0, performance.now() - startAt) : undefined;
	};

	return {
		onNetworkSuccess: (resultCount: number) => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			// Update state BEFORE firing event (prevents duplicates under re-entrancy)
			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'completed',
			};

			fireAnalyticsEvent(
				onTaskSuccess({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						resultCount,
						timeTaken: getTimeTaken(),
						cacheAvailable: state.cacheAvailable,
					},
				}),
			);
		},

		onNetworkFail: (errorName: string, errorMessage: string) => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			// Update state BEFORE firing event (prevents duplicates under re-entrancy)
			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'failed',
			};

			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						errorName,
						errorMessage,
						timeTaken: getTimeTaken(),
						cacheAvailable: state.cacheAvailable,
					},
				}),
			);
		},

		onNetworkAbort: (reason: 'unmounted' | 'superseded') => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'aborted',
			};

			fireAnalyticsEvent(
				onTaskAbort({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						reason,
						cacheAvailable: state.cacheAvailable,
					},
				}),
			);
		},
	};
}

export function createFullPageSearchVitalStats({
	searchSessionId,
	fireAnalyticsEvent,
	fullPageSearchTaskIdRef,
	fullPageSearchNetworkRefs,
	cacheAvailable,
	isNetworkMode,
}: CreateFullPageSearchVitalStatsArgs) {
	const startUxTask = (uxTaskId: string) => {
		fullPageSearchTaskIdRef.current = uxTaskId;

		if (searchSessionId && fg('rovo-search-vitalstats-fullpage-search-ux')) {
			fireAnalyticsEvent(
				onTaskStart({
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX,
				}),
			);
		}
	};

	const abortSupersededNetworkTaskIfNeeded = () => {
		if (!searchSessionId || !isNetworkMode) {
			return;
		}

		createFullPageSearchNetworkVitalStats({
			searchSessionId,
			fireAnalyticsEvent,
			fullPageSearchNetworkRefs,
		}).onNetworkAbort('superseded');
	};

	const startNetworkTask = (uxTaskId: string) => {
		if (!searchSessionId || !isNetworkMode || !fg('rovo-search-vitalstats-fullpage-search-network')) {
			return;
		}

		const networkTaskId = crypto.randomUUID();
		fullPageSearchNetworkRefs.current.taskId = networkTaskId;
		fullPageSearchNetworkRefs.current.startAt = performance.now();

		// Update state BEFORE firing event
		fullPageSearchNetworkRefs.current.state = {
			taskId: networkTaskId,
			uxTaskId, // correlate with UX task
			cacheAvailable,
			state: 'started',
		};

		fireAnalyticsEvent(
			onTaskStart({
				searchSessionId,
				taskId: networkTaskId,
				task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
				moreContext: {
					uxTaskId,
					cacheAvailable,
				},
			}),
		);
	};

	return {
		/**
		 * Called when a new search is initiated.
		 * Handles:
		 * - UX taskStart
		 * - Network taskAbort (superseded)
		 * - Network taskStart
		 */
		onSearchStart: () => {
			const uxTaskId = crypto.randomUUID();
			startUxTask(uxTaskId);

			abortSupersededNetworkTaskIfNeeded();
			startNetworkTask(uxTaskId);

			return uxTaskId;
		},
	};
}
